import { useEffect } from 'react'
import { ConfigProvider } from 'antd'
import arEG from 'antd/locale/ar_EG'
import frFR from 'antd/locale/fr_FR'
import dayjs from 'dayjs'
import 'dayjs/locale/ar'
import 'dayjs/locale/fr'
import { getElevation, lightColors } from '@horizon/tokens'
import { useAdminLocale } from '@/providers/i18n-provider'

const raisedShadow = getElevation('light', 'raised').shadow?.color ?? 'transparent'
const overlayShadow = getElevation('light', 'overlay').shadow?.color ?? 'transparent'

const cssVariables = {
  '--admin-canvas': lightColors.bgCanvas,
  '--admin-subtle': lightColors.bgSubtle,
  '--admin-border': lightColors.borderDefault,
  '--admin-text-primary': lightColors.textPrimary,
  '--admin-text-secondary': lightColors.textSecondary,
  '--admin-primary': lightColors.accentPrimary,
  '--admin-primary-hover': lightColors.accentPrimaryHover,
  '--admin-primary-shadow': lightColors.accentPrimarySubtle,
  '--admin-on-primary': lightColors.accentOnPrimary,
  '--admin-success': lightColors.statusSuccess,
  '--admin-shadow': raisedShadow,
  '--admin-shadow-strong': overlayShadow,
} as const

/** Keeps Ant Design's language and directional primitives in sync with Refine. */
export const AdminLocaleProvider = ({ children }: { children: React.ReactNode }) => {
  const locale = useAdminLocale()
  const direction = locale === 'ar' ? 'rtl' : 'ltr'

  useEffect(() => {
    // Month and weekday names come from dayjs, not from antd's locale bundle.
    dayjs.locale(locale)
    const root = document.documentElement
    root.lang = locale
    root.dir = direction
    Object.entries(cssVariables).forEach(([name, value]) => root.style.setProperty(name, value))
  }, [direction, locale])

  return (
    <ConfigProvider
      direction={direction}
      locale={locale === 'ar' ? arEG : frFR}
      theme={{
        token: {
          fontFamily:
            locale === 'ar'
              ? "'IBM Plex Sans Arabic', 'Sora', system-ui, sans-serif"
              : "'Sora', system-ui, sans-serif",
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}
